import api from './enhancedApiService';
import logger from '../utils/logger';
import { isPrivacyCategoryAllowed } from '@/privacy/privacyPreferences'

/**
 * Analytics Service
 * Handles blog analytics API calls
 */

/**
 * Check if user is authenticated (for analytics tracking)
 * Only track analytics for authenticated users to protect sensitive data
 */
function isAuthenticated() {
  try {
    // Check localStorage for cached user data
    const cachedUser = localStorage.getItem('user')
    if (cachedUser) {
      try {
        const user = JSON.parse(cachedUser)
        return !!user && !!user.id
      } catch {
        return false
      }
    }
    return false
  } catch {
    return false
  }
}

/**
 * Track analytics event
 * @param {Object} data - Event data
 * @returns {Promise<void>}
 */
export const trackAnalyticsEvent = async (data) => {
  if (!isPrivacyCategoryAllowed('analytics')) return

  // Only track analytics for authenticated users
  if (!isAuthenticated()) {
    return
  }

  try {
    await api.post('/blog/analytics/track', data);
  } catch (error) {
    // Silently fail - don't break the app for analytics
    logger.error('Track analytics event error:', error);
  }
};

/**
 * Track post view
 * @param {string} postId - Post ID
 * @param {string} sessionId - Session ID
 * @returns {Promise<void>}
 */
export const trackPostView = async (postId, sessionId) => {
  if (!isPrivacyCategoryAllowed('analytics')) return

  try {
    await api.post(`/blog/posts/${postId}/view`, { sessionId });
  } catch (error) {
    // Silently fail - don't break the app for analytics
    logger.error('Track post view error:', error);
  }
};

/**
 * Track scroll depth
 * @param {string} postId - Post ID
 * @param {string} sessionId - Session ID
 * @param {number} depth - Scroll depth percentage (25, 50, 75, 100)
 * @returns {Promise<void>}
 */
export const trackScrollDepth = async (postId, sessionId, depth) => {
  await trackAnalyticsEvent({
    event: 'scroll_depth',
    postId,
    sessionId,
    depth
  });
};

/**
 * Track reading time
 * @param {string} postId - Post ID
 * @param {string} sessionId - Session ID
 * @param {number} timeSpent - Time spent in seconds
 * @returns {Promise<void>}
 */
export const trackReadingTime = async (postId, sessionId, timeSpent) => {
  await trackAnalyticsEvent({
    event: 'reading_time',
    postId,
    sessionId,
    metric: 'time_spent',
    value: timeSpent
  });
};

/**
 * Track web vitals (performance metrics)
 * @param {Object} metric - Web vital metric
 * @returns {Promise<void>}
 */
export const trackWebVital = async (metric) => {
  try {
    await trackAnalyticsEvent({
      event: 'web_vital',
      metric: metric.name,
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta
    });
  } catch (error) {
    // Silently fail - don't break the app for analytics
    logger.error('Track web vital error:', error);
  }
};

export default {
  trackAnalyticsEvent,
  trackPostView,
  trackScrollDepth,
  trackReadingTime,
  trackWebVital
};
